import React, { useState } from 'react';
import { Alert, Button, Card, Form, Tab, Tabs } from 'react-bootstrap';
import TagsInput from 'react-tagsinput';
import {
  getPresignedFile,
  uploadFile
} from 'services/files/fileElvisService';

const defaultUploadForm = {
  file: null,
  description: '',
  keywords: [],
  active: true
};

const extractUploadedId = payload => {
  if (!payload || typeof payload !== 'object') return '';
  const candidates = [payload.id, payload?.data?.id];
  const found = candidates.find(
    value => value !== undefined && value !== null && value !== ''
  );
  return found !== undefined ? String(found) : '';
};

const extractPresignedUrl = payload => {
  if (typeof payload === 'string') return payload;
  if (!payload || typeof payload !== 'object') return '';
  return (
    payload.url ||
    payload.presignedUrl ||
    payload.preSignedUrl ||
    payload?.data?.url ||
    ''
  );
};

const isImageUrl = url =>
  /\.(png|jpe?g|gif|bmp|webp|svg)(\?|$)/i.test(url || '');

const FileUploadViewer = () => {
  const [uploadForm, setUploadForm] = useState(defaultUploadForm);
  const [uploadValidated, setUploadValidated] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');

  const [id, setId] = useState('');
  const [fetchingPresigned, setFetchingPresigned] = useState(false);
  const [presignedError, setPresignedError] = useState('');
  const [presignedUrl, setPresignedUrl] = useState('');

  const handleUploadFormChange = event => {
    const { name, type, checked, value, files } = event.target;
    if (uploadError) setUploadError('');

    setUploadForm(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : type === 'file'
          ? files?.[0] ?? null
          : value
    }));
  };

  const handleKeywordsChange = keywords => {
    if (uploadError) setUploadError('');
    setUploadForm(prev => ({ ...prev, keywords }));
  };

  const handleUpload = async event => {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity() === false || uploadForm.keywords.length === 0) {
      event.stopPropagation();
      setUploadValidated(true);
      return;
    }
    try {
      setUploading(true);
      setUploadValidated(true);
      setUploadError('');
      setUploadMessage('');
      const formData = new FormData();
      formData.append('File', uploadForm.file);
      formData.append('Description', uploadForm.description.trim());
      formData.append('Keywords', uploadForm.keywords.join(','));
      formData.append('Active', String(uploadForm.active));
      const result = await uploadFile(formData);
      setUploadMessage('File uploaded successfully.');
      const extractedId = extractUploadedId(result);
      if (extractedId) setId(extractedId);
    } catch (err) {
      setUploadError('Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleFetchPresigned = async event => {
    event.preventDefault();
    if (!id) return;
    try {
      setFetchingPresigned(true);
      setPresignedError('');
      setPresignedUrl('');
      const result = await getPresignedFile(id);
      const url = extractPresignedUrl(result);
      if (!url) throw new Error('No file URL returned.');
      setPresignedUrl(url);
    } catch (err) {
      if (err?.response?.status === 404) setPresignedError('File not found.');
      else setPresignedError('Unable to load file.');
    } finally {
      setFetchingPresigned(false);
    }
  };

  return (
    <Tabs defaultActiveKey="upload" id="file-upload-viewer-tabs" className="mb-3">
      <Tab eventKey="upload" title="Upload">
        <Card>
          <Card.Body>
            {uploadMessage ? (
              <Alert variant="success">{uploadMessage}</Alert>
            ) : null}
            {uploadError ? <Alert variant="danger">{uploadError}</Alert> : null}
            <Form
              noValidate
              validated={uploadValidated}
              onSubmit={handleUpload}
            >
              <Form.Group className="mb-3">
                <Form.Label htmlFor="filesTestFile">File</Form.Label>
                <Form.Control
                  id="filesTestFile"
                  name="file"
                  type="file"
                  onChange={handleUploadFormChange}
                  disabled={uploading}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please choose a file to upload.
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="filesTestDescription">
                  Description
                </Form.Label>
                <Form.Control
                  id="filesTestDescription"
                  name="description"
                  type="text"
                  value={uploadForm.description}
                  onChange={handleUploadFormChange}
                  placeholder="Enter a description"
                  disabled={uploading}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a description.
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="filesTestKeywords">Keywords</Form.Label>
                <TagsInput
                  value={uploadForm.keywords}
                  onChange={handleKeywordsChange}
                  disabled={uploading}
                  addOnBlur
                  addOnPaste
                  onlyUnique
                  tagProps={{
                    className:
                      'react-tagsinput-tag bg-primary bg-gradient text-light shadow-sm badge py-2',
                    classNameRemove: 'react-tagsinput-remove'
                  }}
                  inputProps={{
                    id: 'filesTestKeywords',
                    className: 'react-tagsinput-input',
                    style: { width: '16rem' },
                    placeholder: 'Type a keyword and press Enter'
                  }}
                />
                {uploadValidated && uploadForm.keywords.length === 0 ? (
                  <div className="invalid-feedback" style={{ display: 'block' }}>
                    Please enter at least one keyword.
                  </div>
                ) : null}
              </Form.Group>
              <Form.Check
                id="filesTestActive"
                name="active"
                type="switch"
                label="Active"
                checked={uploadForm.active}
                onChange={handleUploadFormChange}
                disabled={uploading}
                className="mb-3"
              />
              <Button type="submit" variant="primary" disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </Tab>
      <Tab eventKey="view" title="View">
        <Card>
          <Card.Body>
            {presignedError ? (
              <Alert variant="danger">{presignedError}</Alert>
            ) : null}
            <Form onSubmit={handleFetchPresigned}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="filesTestFileId">File ID</Form.Label>
                <Form.Control
                  id="filesTestFileId"
                  name="id"
                  type="text"
                  value={id}
                  onChange={event => setId(event.target.value)}
                  placeholder="Enter the file's ID"
                  disabled={fetchingPresigned}
                />
              </Form.Group>
              <Button
                type="submit"
                variant="primary"
                disabled={fetchingPresigned || !id}
              >
                {fetchingPresigned ? 'Loading...' : 'View File'}
              </Button>
            </Form>
            {presignedUrl ? (
              <div style={{ marginTop: '1rem' }}>
                <a href={presignedUrl} target="_blank" rel="noreferrer">
                  Open file in new tab
                </a>
                {isImageUrl(presignedUrl) ? (
                  <div style={{ marginTop: '0.75rem' }}>
                    <img
                      src={presignedUrl}
                      alt="File preview"
                      style={{ maxWidth: '100%', borderRadius: '0.375rem' }}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </Card.Body>
        </Card>
      </Tab>
    </Tabs>
  );
};

export default FileUploadViewer;
